import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, MapPin, Sparkles, ArrowRightLeft, Loader2, Info,
  Trophy, Languages, RefreshCw,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Phase 1E.8 — Smart Matches.
 *
 * Read-only compatibility feed. NO contact / message / trade / friend
 * buttons. Each card links to the compatible collector's public profile.
 * The engine is fully live — a Refresh button re-computes on demand.
 */
export default function SmartMatches() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [refreshing, setRefreshing] = useState(false);
  const [filterType, setFilterType] = useState("all");

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/me/smart-matches?limit=100&min_score=1");
      setState({ loading: false, data: r.data, error: null });
    } catch (e) {
      setState({ loading: false, data: null,
                  error: e?.response?.data?.detail || "Could not load matches." });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const filtered = useMemo(() => {
    const all = state.data?.matches || [];
    if (filterType === "mutual") return all.filter((m) => m.match_type === "mutual");
    if (filterType === "one_way") return all.filter((m) => m.match_type === "one_way");
    return all;
  }, [state.data, filterType]);

  if (state.loading) {
    return (
      <div className="py-16 text-center text-[var(--smoke)]" data-testid="smart-loading">
        <Loader2 className="inline animate-spin mr-2" size={16} /> Finding your matches…
      </div>
    );
  }

  const d = state.data || {};
  if (!d.eligible) {
    return <IneligibleView reason={d.reason} />;
  }

  return (
    <div data-testid="smart-matches" className="space-y-6 pb-16">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-[var(--ink)] inline-flex items-center gap-2">
            <Sparkles size={22} className="text-[var(--panini-red)]" /> Smart Matches
          </h1>
          <p className="text-sm text-[var(--smoke)] mt-1 max-w-2xl">
            Collectors who have stickers you need — and vice versa. Live-computed
            from your current inventory. Nothing is saved. Contact tools are
            coming in a future phase.
          </p>
        </div>
        <button
          type="button"
          data-testid="smart-refresh"
          onClick={refresh}
          disabled={refreshing}
          className="btn-ghost text-xs inline-flex items-center gap-2"
        >
          {refreshing
            ? <Loader2 size={13} className="animate-spin" />
            : <RefreshCw size={13} />}
          Refresh
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="smart-summary">
        <SummaryTile testid="sum-total"  label="Matches" value={d.matches?.length || 0} />
        <SummaryTile testid="sum-mutual" label="Mutual"
                      value={(d.matches || []).filter((m) => m.match_type === "mutual").length} />
        <SummaryTile testid="sum-oneway" label="One-way"
                      value={(d.matches || []).filter((m) => m.match_type === "one_way").length} />
        <SummaryTile testid="sum-scanned" label="Scanned" value={d.candidates_scanned || 0} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter matches">
        {[
          { v: "all",     label: "All" },
          { v: "mutual",  label: "Mutual only" },
          { v: "one_way", label: "One-way only" },
        ].map((f) => (
          <button
            key={f.v}
            type="button"
            data-testid={`smart-filter-${f.v}`}
            onClick={() => setFilterType(f.v)}
            className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold uppercase tracking-widest transition ${
              filterType === f.v
                ? "bg-[var(--panini-red)] text-white border-[var(--panini-red)]"
                : "bg-white text-[var(--ink)] border-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Results */}
      {state.error && (
        <div data-testid="smart-error"
              className="rounded-2xl border-2 border-red-500 bg-red-50 p-4 text-sm text-red-700">
          {state.error}
        </div>
      )}
      {filtered.length === 0 ? (
        <EmptyState hasAny={(d.matches || []).length > 0} filter={filterType} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="smart-results">
          {filtered.map((m) => (
            <MatchCard key={m.collector.username} match={m} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
function IneligibleView({ reason }) {
  const copy = {
    matching_disabled:        {
      title:  "Turn on matching to see compatible collectors",
      body:   "Head to Privacy & Matching and flip “Include me in future match searches” on.",
      cta:    { to: "/dashboard/matching", label: "Open Privacy & Matching" },
    },
    share_missing_disabled:   {
      title:  "Share your missing list to be matched",
      body:   "Matching needs to know what you're looking for. Enable it under Privacy & Matching.",
      cta:    { to: "/dashboard/matching", label: "Open Privacy & Matching" },
    },
    share_duplicate_disabled: {
      title:  "Share your duplicate list to be matched",
      body:   "Matching pairs your extras with what others need. Enable it under Privacy & Matching.",
      cta:    { to: "/dashboard/matching", label: "Open Privacy & Matching" },
    },
    profile_not_public:       {
      title:  "Make your profile public",
      body:   "Only public collectors can appear in matching. Update your profile privacy.",
      cta:    { to: "/dashboard/collector-profile", label: "Open Profile" },
    },
    location_missing:         {
      title:  "Set your country",
      body:   "Matching uses your coarse location to rank nearby collectors first. Country is enough.",
      cta:    { to: "/dashboard/matching", label: "Open Privacy & Matching" },
    },
  }[reason] || {
    title: "Matching isn't available for your account",
    body:  "If this looks wrong, check Privacy & Matching.",
    cta:   { to: "/dashboard/matching", label: "Open Privacy & Matching" },
  };
  return (
    <div className="max-w-2xl mx-auto rounded-2xl border-2 border-[var(--ink)] bg-white p-8 shadow-[4px_4px_0_var(--ink)] text-center"
          data-testid={`smart-ineligible-${reason || "unknown"}`}>
      <div className="w-14 h-14 mx-auto rounded-full bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)] grid place-items-center mb-3">
        <Info size={22} className="text-[var(--ink)]" />
      </div>
      <h2 className="font-display text-2xl text-[var(--ink)]">{copy.title}</h2>
      <p className="text-sm text-[var(--smoke)] mt-2">{copy.body}</p>
      <Link
        to={copy.cta.to}
        data-testid="smart-ineligible-cta"
        className="btn-red mt-5 inline-flex text-sm"
      >
        {copy.cta.label}
      </Link>
    </div>
  );
}

function EmptyState({ hasAny, filter }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-[var(--ink)]/40 bg-white p-8 text-center"
          data-testid="smart-empty">
      <Users size={28} className="mx-auto text-[var(--smoke)] mb-2" />
      {hasAny
        ? (<p className="text-sm text-[var(--smoke)]">No {filter === "mutual" ? "mutual" : "one-way"} matches yet. Try a different filter.</p>)
        : (<p className="text-sm text-[var(--smoke)]">No matches yet. Try marking more stickers as MISSING or DUPLICATE, or check back later.</p>)
      }
    </div>
  );
}

function SummaryTile({ label, value, testid }) {
  return (
    <div data-testid={testid}
          className="rounded-2xl border-2 border-[var(--ink)] bg-white px-4 py-3 shadow-[4px_4px_0_var(--ink)]">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">{label}</div>
      <div className="text-3xl font-display text-[var(--ink)]">{value}</div>
    </div>
  );
}

function MatchCard({ match }) {
  const c = match.collector;
  const isMutual = match.match_type === "mutual";
  const locationLine = c.location
    ? [c.location.city, c.location.state_province, c.location.country].filter(Boolean).join(", ")
    : null;
  const distanceLine = (() => {
    const d = match.distance || {};
    if (d.km !== null && d.km !== undefined) {
      return `≈ ${Math.round(d.km)} km away`;
    }
    return ({
      same_city:         "Same city",
      same_state:        "Same state / region",
      same_country:      "Same country",
      different_country: "Different country",
    })[d.level] || null;
  })();

  return (
    <div data-testid={`match-card-${c.username}`}
          className="rounded-2xl border-2 border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--ink)]">
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] overflow-hidden grid place-items-center shrink-0">
          {c.avatar_url ? (
            <img src={c.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <Users size={20} className="text-[var(--ink)]/60" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <Link to={`/collectors/${c.username}`}
                 data-testid={`match-collector-link-${c.username}`}
                 className="font-display text-lg text-[var(--ink)] hover:text-[var(--panini-red)] transition">
            {c.display_name || c.username}
          </Link>
          <div className="text-xs text-[var(--smoke)]">@{c.username}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
            {locationLine && (
              <span className="inline-flex items-center gap-1 text-[var(--smoke)]"
                    data-testid="match-location">
                <MapPin size={11} /> {locationLine}
              </span>
            )}
            {distanceLine && (
              <span className="inline-flex items-center gap-1 text-[var(--smoke)]"
                    data-testid="match-distance">
                {distanceLine}
              </span>
            )}
          </div>
        </div>
        <ScoreBadge score={match.score} mutual={isMutual} />
      </div>

      {/* Exchange stats */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <ExchangeTile testid="tile-b-helps-a"
                       label="They can give you"
                       count={match.b_helps_a}
                       color="red" />
        <ExchangeTile testid="tile-a-helps-b"
                       label="You can give them"
                       count={match.a_helps_b}
                       color={isMutual ? "yellow" : "grey"} />
      </div>

      {/* Type + language chips */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span data-testid="match-type-badge"
               className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border-2 border-[var(--ink)] ${
                 isMutual ? "bg-[var(--panini-red)] text-white" : "bg-white text-[var(--ink)]"
               }`}>
          {isMutual ? <><ArrowRightLeft size={11} className="inline mr-1" /> Mutual</>
                    : <><Trophy size={11} className="inline mr-1" /> One-way</>}
        </span>
        {(match.shared_languages || []).length > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-[var(--ink)]"
                 data-testid="match-languages">
            <Languages size={12} />
            {match.shared_languages.map((l) => l.toUpperCase()).join(" · ")}
          </span>
        )}
      </div>
    </div>
  );
}

function ScoreBadge({ score, mutual }) {
  const color = score >= 80 ? "text-[var(--panini-red)]"
              : score >= 50 ? "text-[var(--ink)]"
              : "text-[var(--smoke)]";
  return (
    <div data-testid="match-score"
          className={`shrink-0 text-right ${color}`}>
      <div className="text-3xl font-display leading-tight">{Math.round(score)}</div>
      <div className="text-[9px] font-bold uppercase tracking-widest">
        {mutual ? "Mutual score" : "Score"}
      </div>
    </div>
  );
}

function ExchangeTile({ label, count, color, testid }) {
  const bg = color === "red"
      ? "bg-[var(--panini-red)]/10 border-[var(--panini-red)]"
      : color === "yellow"
      ? "bg-[var(--panini-yellow-soft)] border-[var(--ink)]"
      : "bg-[var(--cream)] border-[var(--ink)]/40";
  return (
    <div data-testid={testid}
          className={`rounded-xl border-2 px-3 py-2 ${bg}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">
        {label}
      </div>
      <div className="text-2xl font-display text-[var(--ink)]">
        {count}<span className="text-xs text-[var(--smoke)] ml-1">sticker{count === 1 ? "" : "s"}</span>
      </div>
    </div>
  );
}
